import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  CompanyCountriesDto,
  CompanyId,
  CreateGoal$Request,
  CustomType,
  DivisionCategoryId,
  DivisionCategoryRow,
  GoalId,
  GoalWithProgress,
  ProgressType,
  UpdateGoal$Request,
  UserCompanyDto,
  UserProfileDto,
} from '../../services/api.types'
import { useForm } from 'react-hook-form'
import { Card } from '../../components/Card/Card'
import { t } from 'ttag'
import dayjs from '../../dayjsConfig'
import { gray } from '../../styles/material-ui-theme-v2'
import { Grid, LinearProgress } from '@material-ui/core'
import { CustomTypeCategory, customUnits, getCustomUnitShortTextById } from '../../services/CustomTypes'
import './ChallengeAdmin.scss'
import { convertLocalToUTCDate, convertUTCToLocalDate, getTimePeriodNextMonth } from '../../common/helpers/date'
import Hidden from '@material-ui/core/Hidden'
import ChallengeReview from './components/ChallengeReview/ChallengeReview'
import {
  buildDivisionCategoryLeaderboardId,
  getAccountEndDate,
  getAccountStartDate,
  getContractPeriod,
  isActivityIndexType,
  isCollectiveType,
  isCustomType,
  isDivisionCategoryLeaderboardId,
  isPersistenceType,
  isSimpleDayType,
} from '../../common/helpers/challenges'
import Typography from '@material-ui/core/Typography'

import { isRtl } from '../../i18nInit'
import { Capacitor, PluginListenerHandle } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'
import { scrollToBottom, scrollToTop } from '../../common/helpers/scroll'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import {
  getLocalizedDisplayUnit,
  unitsCountDurationMinuteLong,
  unitsCountEnergyKilocalorieShort,
} from '../../common/helpers/units'
import { activityTypes, activityTypesWithCustomAndBonus } from './activityTypes'
import { store } from '../../index'
import { go, push } from 'connected-react-router'
import { ActivityView } from '../Activity/store/ActivityReducer'
import ChallengeStepOrganizer from './components/ChallengeStepOrganizer/ChallengeStepOrganizer'
import ChallengeStepPeriod from './components/ChallengeStepPeriod/ChallengeStepPeriod'
import ChallengeStepDetails from './components/ChallengeStepDetails/ChallengeStepDetails'
import ChallengeStepGoal from './components/ChallengeStepGoal/ChallengeStepGoal'
import ChallengeStepActivityType from './components/ChallengeStepActivityType/ChallengeStepActivityType'
import ChallengeStepLeaderboards from './components/ChallengeStepLeaderboards/ChallengeStepLeaderboards'
import ChallengeStepChallengeType from './components/ChallengeStepChallengeType/ChallengeStepChallengeType'
import ChallengeStepConfiguration from './components/ChallengeStepConfiguration/ChallengeStepConfiguration'
import ChallengeStepPoints from './components/ChallengeStepPoints/ChallengeStepPoints'
import ChallengeStepTotalPoints from './components/ChallengeStepTotalPoints/ChallengeStepTotalPoints'
import { PointsLogicTemplate } from './components/ChallengePoints/ChallengePoints'
import YumCancelButton from '../../components/YumCancelButton/YumCancelButton'
import YumSubmitButton from '../../components/YumSubmitButton/YumSubmitButton'
import { filterTotalChallengesThatCover } from './components/ChallengeTotalPoints/ChallengeTotalPointsIncludes'

export interface IChallengeAdminDispatchProps {
  createGoal(payload: CreateGoal$Request): void
  updateGoal(payload: UpdateGoal$Request): void
  getCurrentCompanyGoal(payload: { id?: GoalId | null; silentGoalUpdate?: boolean; token?: string | null }): void
  getDivisionCategories: (payload?: { companyId?: CompanyId }) => void
  resetSubmitInProgress: () => void
}

export interface IChallengeAdminProps extends IChallengeAdminDispatchProps {
  goalId?: GoalId
  managedCompanies: UserCompanyDto[]
  userCompanies: UserCompanyDto[]
  goals: GoalWithProgress[]
  userDto: UserProfileDto
  divisionCategories: DivisionCategoryRow[]
  companyCountries: CompanyCountriesDto[]
  submitInProgress: boolean
}

export type GoalType = '' | 'totalAmount' | 'consistentDays' | 'collective' | 'noGoal'

// Prefer imperial defaults for US time zones when possible.
const timeZoneGuess = dayjs.tz.guess()
const isUsa = timeZoneGuess.startsWith('America/') || timeZoneGuess.startsWith('US/')

// Goal configuration for team, group, and country goals
export type GoalConfig = {
  enabled: boolean
  totalGoal: number | ''
  //dailyGoal: number | ''
  persistenceGoal: number | ''
  indexTarget: number | ''
  //avg?: boolean
  type?: DivisionCategoryId | string | null
  groupCategoryId?: DivisionCategoryId | null
}

export type GoalsConfig = {
  individual: GoalConfig | null
  collective: GoalConfig | null
  team: GoalConfig | null
  country: GoalConfig | null
  group: Record<DivisionCategoryId, GoalConfig | null>
}

export type FormData = {
  maxFormStep: number

  // Step 0.
  companyId: CompanyId | 'new'
  companyName: string
  newCommunityName: string
  isUserCreatedChallenge: boolean

  // Step 1.
  activityType: ProgressType | CustomTypeCategory | null
  customActivityName?: string

  // Step 2.
  leaderboards: string[] // Ordered list of enabled leaderboards
  leaderboardsDraft: Array<{ id: string; enabled: boolean }> // Temporary ordered list with enabled state
  //asCollectiveUnit: boolean
  chatEnabled: boolean
  manualEntryEnabled: boolean
  roundDailyPoints: boolean
  autoJoinEnabled: boolean
  maxManualEntryDays: number

  challengePoints: Array<any>
  challengeTotalPoints: Array<any>
  includeInTotalPointsChallenges: Record<GoalId, boolean>

  // Step 3.
  dateRange: [Date | undefined, Date | undefined]
  days: number

  // Step 4.
  goalType: GoalType
  // Deprecated
  totalGoal: number | ''
  // Deprecated
  dailyMax: number | ''
  // Deprecated
  collectiveGoal: number | ''
  // Deprecated
  persistenceGoal: number | ''
  // Deprecated
  dailyGoal: number | ''

  customUnit: string | null
  customType: CustomType | null
  // Deprecated
  indexTarget: number

  // SUM or AVG aggregation method
  avgAgg: boolean

  // NEW: Team, group, and country goals - each can only have one goal
  goals: GoalsConfig

  // Configuration
  simpleManualEntry: boolean
  showGroupMembers: boolean
  showGroupMembersContributions: boolean
  hideLeaderboardTables: boolean

  // Step 5.
  title: string
  prize: string
  description: string
}

export const dailyGoalUnit = (activityType: any, customType: string | null, customUnit?: string | null) => {
  //return activityType + ' ' + customType
  return customType === CustomTypeCategory.CUSTOM || customType === ProgressType.PERSISTENCE_CUSTOM
    ? customUnit
    : activityType === CustomTypeCategory.STEPS
      ? t`steps`
      : activityType === CustomTypeCategory.INTENSIVE_MINUTES
        ? unitsCountDurationMinuteLong
        : activityType === CustomTypeCategory.CALORIES
          ? unitsCountEnergyKilocalorieShort
          : activityType === CustomTypeCategory.ALL
            ? '%'
            : customType
              ? getCustomUnitShortTextById(
                  customType,
                ) /*+ ' ' + t`of` + ' ' + getCustomTypeTextById(activityType)?.toLowerCase()*/
              : ''
}

export const consistencyUnit = (days: number) => {
  if (!days) return ''
  // translator: [15] out of {30} days
  return t`out of ${days} days`
}

export const calculateDuration = (startDate: Date | undefined, endDate: Date | undefined) => {
  let days = 0
  if (startDate && endDate) {
    // Needs to not work with Daylight Saving Time.
    days = Math.round(Math.abs(endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24))
  }
  return days
}

export const goalsConfigDefaults = (goal: GoalWithProgress): GoalsConfig => {
  const goalsConfigString = goal.goals
  let goalsConfig = JSON.parse(goalsConfigString) as GoalsConfig
  // Backwards compatibility.
  if (!goalsConfig) {
    // Collective
    if (isCollectiveType(goal.progressType)) {
      goalsConfig = {
        individual: null,
        team: null,
        group: {},
        country: null,
        collective: {
          enabled: true,
          totalGoal: goal.collectiveGoal || '',
          //dailyGoal: '',
          persistenceGoal: '',
          indexTarget: '',
        },
      }
      // Consistency
    } else if (isPersistenceType(goal.progressType)) {
      goalsConfig = {
        individual: {
          enabled: true,
          totalGoal: goal.customGoal || '',
          //dailyGoal: goal.dailyPersistenceGoal || '',
          persistenceGoal: goal.persistenceGoal || '',
          indexTarget: '', //goal.activityFormulaTarget || '',
        },
        team: null,
        group: {},
        country: null,
        collective: null,
      }
      // Total
    } else {
      goalsConfig = {
        individual: {
          enabled: true,
          totalGoal: goal.customGoal || '',
          //dailyGoal: goal.dailyPersistenceGoal || '',
          persistenceGoal: goal.persistenceGoal || '',
          indexTarget: goal.activityFormulaTarget || '',
        },
        team: null,
        group: {},
        country: null,
        collective: null,
      }
    }
  }
  return goalsConfig
}
const leaderboardDefaults = (goal: GoalWithProgress, goalsConfig: GoalsConfig | null | undefined): string[] => {
  // Read from the goal object directly to keep the call site tiny.
  const { leaderboards: goalLeaderboards, hidePeopleTab, hideGroupsTab, hideCountriesTab, progressType } = goal

  // Parse the leaderboards list.
  // New format: ordered list of ActivityView values and "DIVISION_CATEGORY_*" IDs.
  // Legacy format: list of raw DivisionCategory IDs.
  const parseLeaderboardsRaw = (value: unknown): string[] => {
    // Keep parsing rules tiny and defensive to avoid breaking older data.
    if (Array.isArray(value)) return value as string[]
    if (!value) return []
    try {
      return JSON.parse(value as any) as string[]
    } catch (e) {
      return []
    }
  }
  const leaderboardsRaw = parseLeaderboardsRaw(goalLeaderboards)

  // Detect the new format so we do not reorder already-normalized data.
  const hasNewFormatLeaderboards = leaderboardsRaw.some(
    (leaderboardId) =>
      Object.values(ActivityView).includes(leaderboardId as ActivityView) ||
      isDivisionCategoryLeaderboardId(leaderboardId),
  )

  // Build a normalized ordered list for the form.
  const legacyLeaderboards: string[] = [
    // Keep the legacy order consistent with the UI defaults.
    ...(!hidePeopleTab ? [ActivityView.INDIVIDUAL] : []),
    ...(!hideGroupsTab ? [ActivityView.GROUP] : []),
    ...leaderboardsRaw.map((categoryId) => buildDivisionCategoryLeaderboardId(categoryId)),
    ...(!hideCountriesTab ? [ActivityView.COUNTRY] : []),
  ]
  // Keep the legacy ordering only when the data is in the old shape.
  const leaderboards: string[] = hasNewFormatLeaderboards ? leaderboardsRaw : legacyLeaderboards

  // Legacy data never stored a collective tab, so add it when applicable.
  // Actually, don't add collectie tab for legacy challenges!
  // const shouldAddCollective =
  //   !hasNewFormatLeaderboards && (goalsConfig?.collective?.enabled || isCollectiveType(progressType))
  // if (shouldAddCollective) leaderboards.push(ActivityView.COLLECTIVE)

  // Always keep at least one enabled leaderboard in the form state.
  // This protects the UI from rendering an empty tabs list.
  if (leaderboards.length === 0) {
    leaderboards.push(ActivityView.INDIVIDUAL)
  }

  return leaderboards
}

const getDefaultValues = (goals: GoalWithProgress[], goal?: GoalWithProgress) => {
  if (!goal) return {}
  const {
    id: goalId,
    voucher,
    progressType,
    endDate: endDateStamp,
    startDate: startDateStamp,
    companyId,
    ownerUserId,
    companyName,
    divisionId,
    dailyStepsGoal,
    activityFormulaTarget,
    collectiveGoal,
    title,
    hidePeopleTab,
    hideGroupsTab,
    hideCountriesTab,
    chatEnabled,
    dailyPersistenceGoal,
    persistenceGoal,
    customType,
    customName,
    customUnit,
    customGoal,
    description,
    manualEntryEnabled,
    roundDailyPoints,
    autoJoinEnabled,
    maxManualEntryDays,
    stepsGoal,
    dailyMax,
    leaderboards: goalLeaderboards,
    //goals: goalsConfig,
    avgAgg,
  } = goal
  const showGroupMembers = (goal as any).showGroupMembers || false
  const showGroupMembersContributions = (goal as any).showGroupMembersContributions || false
  const hideLeaderboardTables = (goal as any).hideLeaderboardTables || false
  const goalsConfig = goalsConfigDefaults(goal)
  const leaderboards = leaderboardDefaults(goal, goalsConfig)

  let activityType: ProgressType | CustomTypeCategory | null = null
  let simpleManualEntry = goal.simpleManualEntry
  if (customType) {
    if (customType === CustomType.CUSTOM) {
      activityType = CustomTypeCategory.CUSTOM
    } else {
      activityType = customUnits.find((cu) => cu.id === customType)?.category || null
    }
  } else {
    switch (progressType) {
      case ProgressType.ALL:
      case ProgressType.FORMULA:
      case ProgressType.PERSISTENCE_ALL:
        activityType = CustomTypeCategory.ALL
        break
      // Deprecated
      case ProgressType.STEPS:
      case ProgressType.PERSISTENCE_STEPS:
      case ProgressType.COLLECTIVE_STEPS:
        activityType = CustomTypeCategory.STEPS
        break
      // Deprecated
      case ProgressType.CALORIES:
      case ProgressType.PERSISTENCE_CALORIES:
      case ProgressType.COLLECTIVE_CALORIES:
        activityType = CustomTypeCategory.CALORIES
        break
      // Deprecated
      case ProgressType.INTENSIVE_MINUTES:
      case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
      case ProgressType.COLLECTIVE_INTENSIVE_MINUTES:
        activityType = CustomTypeCategory.INTENSIVE_MINUTES
        break
      case ProgressType.CUSTOM:
      case ProgressType.PERSISTENCE_CUSTOM:
      case ProgressType.PERSISTENCE_CUSTOM_BINARY:
        activityType = CustomTypeCategory.CUSTOM
        break
      default:
        alert('Error: unknown progress type')
    }
  }

  let goalType = 'totalAmount'
  if (isPersistenceType(progressType)) goalType = 'consistentDays'

  // Deprecated
  if (progressType === ProgressType.PERSISTENCE_CUSTOM_BINARY) {
    goalType = 'consistentDays'
    simpleManualEntry = true
  }

  if (isCollectiveType(progressType)) goalType = 'collective'

  let totalGoal = null
  if (isCustomType(progressType)) totalGoal = customGoal

  let dailyGoal = dailyPersistenceGoal
  if (progressType === ProgressType.STEPS) dailyGoal = dailyStepsGoal

  const startDate = convertUTCToLocalDate(new Date(startDateStamp))
  const endDate = convertUTCToLocalDate(new Date(endDateStamp))
  const days = calculateDuration(startDate, endDate)

  // Backwards compatibility.
  if (goalType === 'totalAmount' && progressType === ProgressType.STEPS) {
    totalGoal = stepsGoal
  }

  if (goalType === 'totalAmount' && progressType === ProgressType.INTENSIVE_MINUTES) {
    // dailyGoal = Math.floor((customGoal || 0) / days)
    totalGoal = customGoal
  }

  const challengePoints =
    customType === CustomType.POINTS ? JSON.parse(goal.pointsLogic) || defaultChallengePoints : defaultChallengePoints
  const challengeTotalPoints = customType === CustomType.TOTAL_POINTS ? JSON.parse(goal.pointsLogic) || [] : []

  const relevantTotalPointChallenges = filterTotalChallengesThatCover(companyId, goals, startDate, endDate)
  const includeInTotalPointsChallenges = {} as Record<GoalId, boolean>
  relevantTotalPointChallenges.forEach((challenge) => {
    includeInTotalPointsChallenges[challenge.id] = goalId ? challenge.pointsLogic?.includes(goalId) : true
  })

  return {
    maxFormStep: 10,

    // Organizer
    companyId,
    companyName,
    newCommunityName: '',
    isUserCreatedChallenge: !!ownerUserId,

    // Activity type
    activityType,
    customActivityName: customType === CustomType.CUSTOM ? customName : undefined,

    // Period
    dateRange: [startDate, endDate] as [Date | undefined, Date | undefined],
    days,

    // Leaderboards
    leaderboards,

    // Goals
    avgAgg,
    goalType,
    goals: goalsConfig || {
      individual: null,
      collective: null,
      team: null,
      country: null,
      group: {},
    },
    showGroupMembers: false,
    showGroupMembersContributions: false,
    hideLeaderboardTables: false,
    totalGoal: totalGoal || undefined,
    dailyMax,
    collectiveGoal,
    persistenceGoal,
    dailyGoal,
    customUnit,
    customType,
    simpleManualEntry,
    includeInTotalPointsChallenges,

    indexTarget: isPersistenceType(progressType) ? dailyPersistenceGoal : activityFormulaTarget,

    // Configuration
    chatEnabled,
    manualEntryEnabled,
    roundDailyPoints,
    autoJoinEnabled,
    maxManualEntryDays,

    challengePoints,
    challengeTotalPoints,

    showGroupMembers,
    showGroupMembersContributions,
    hideLeaderboardTables,

    // Details
    title,
    prize: voucher,
    description,
  }
}

function checkVisible(element: HTMLDivElement | null) {
  if (element === null) return true
  const rect = element.getBoundingClientRect()
  const viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight)
  return !(rect.bottom < 0 || rect.top - viewHeight >= 0)
}

function useIsVisible(ref: React.RefObject<HTMLDivElement>, formStep: number) {
  const [isVisible, setIsVisible] = useState(checkVisible(ref.current))

  useEffect(() => {
    function handleResize() {
      setIsVisible(checkVisible(ref.current))
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    setTimeout(() => setIsVisible(checkVisible(ref.current)), 0)
  }, [formStep])

  return isVisible
}

let renderCount = 0

const defaultChallengePoints: PointsLogicTemplate[] = [
  {
    category: CustomTypeCategory.STEPS,
    value: 150,
    unit: CustomType.STEPS,
    points: 1,
    customActivityName: '',
    customUnitName: '',
    simpleManualEntry: false,
    //dailyGoal: 10000,
    // description: '',
  },
  {
    category: CustomTypeCategory.EXERCISE,
    value: 1,
    unit: CustomType.EXERCISE_MIN,
    points: 1,
    customActivityName: '',
    customUnitName: '',
    simpleManualEntry: false,
    //dailyGoal: 30,
    // description: '',
  },
]

export const CHALLENGE_ADMIN_STEP_ORGANIZER = 0
export const CHALLENGE_ADMIN_STEP_ACTIVITY_TYPE = 1
export const CHALLENGE_ADMIN_STEP_POINTS = 2
export const CHALLENGE_ADMIN_STEP_PERIOD = 3
export const CHALLENGE_ADMIN_STEP_TOTAL_POINTS = 4
export const CHALLENGE_ADMIN_STEP_LEADERBOARDS = 5
export const CHALLENGE_ADMIN_STEP_CHALLENGE_TYPE = 6
export const CHALLENGE_ADMIN_STEP_GOAL = 7
export const CHALLENGE_ADMIN_STEP_CONFIGURATION = 8
export const CHALLENGE_ADMIN_STEP_DETAILS = 9
export const CHALLENGE_ADMIN_STEP_REVIEW = 10

export default function ChallengeAdmin(props: IChallengeAdminProps) {
  renderCount++
  /* eslint-disable react/prop-types */
  const {
    goalId,
    managedCompanies,
    userCompanies,
    goals,
    getCurrentCompanyGoal,
    divisionCategories,
    getDivisionCategories /*, hasRoleInCompany*/,
    companyCountries,
    resetSubmitInProgress,
  } = props

  const formSteps = 10
  const canChangeOrganizer = /*managedCompanies.length && */ !goalId
  const formStartStep = canChangeOrganizer ? 0 : 1
  const [formStep, setFormStep] = useState(formStartStep)
  const [data, setData] = useState({} as FormData)

  const buttonsRef = useRef<HTMLDivElement>(null)
  const areButtonsVisible = useIsVisible(buttonsRef, formStep)

  const orderedCompanies = managedCompanies.sort((a, b) => {
    return !b.ownerCompanyId === !a.ownerCompanyId ? 0 : !b.ownerCompanyId ? 1 : -1
  })

  const { startDate, endDate } = getTimePeriodNextMonth()
  const {
    register,
    setValue,
    setError,
    clearErrors,
    getValues,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    ...form
  } = useForm<FormData>({
    shouldUnregister: false,
    defaultValues: /*defaultValues ||*/ {
      maxFormStep: 1,
      companyId: orderedCompanies[0] ? orderedCompanies[0].companyId : 'new',
      newCommunityName: '',
      isUserCreatedChallenge: false,
      activityType: CustomTypeCategory.STEPS,
      // Default order for new challenges.
      leaderboards: [ActivityView.INDIVIDUAL],
      leaderboardsDraft: [],
      roundDailyPoints: true,
      simpleManualEntry: false,
      autoJoinEnabled: false,
      //asCollectiveUnit: false,
      chatEnabled: true,
      manualEntryEnabled: true,
      dateRange: [new Date(startDate), new Date(endDate)],
      days: calculateDuration(new Date(startDate), new Date(endDate)),
      customActivityName: undefined,
      persistenceGoal: '',
      goalType: 'totalAmount',
      totalGoal: '',
      dailyMax: '',
      // Deprecated
      collectiveGoal: '',
      // Deprecated
      dailyGoal: '',
      indexTarget: 100,
      customUnit: '',
      customType: null,
      title: '',
      prize: '',
      description: '',
      challengePoints: defaultChallengePoints,
      challengeTotalPoints: [],
      includeInTotalPointsChallenges: {},
      maxManualEntryDays: 7,
      goals: {
        individual: null,
        collective: null,
        team: null,
        country: null,
        group: {},
      },
      showGroupMembers: false,
      showGroupMembersContributions: false,
      hideLeaderboardTables: false,
      avgAgg: false,
    },
  })

  const goal: GoalWithProgress | undefined = goals ? goals.find(({ id }) => id === goalId) : undefined
  // Legacy challenges do not use the compact ranking view.
  // We keep the leaderboards UI in legacy mode to avoid reordering issues.
  const isLegacyChallenge = !!goalId && !goal?.compactRankingView

  // useEffect(() => {
  //   register('goalType', { required: formStep === CHALLENGE_ADMIN_STEP_GOAL })
  // }, [register, formStep])

  useEffect(() => {
    register('goalType', { required: formStep === CHALLENGE_ADMIN_STEP_CHALLENGE_TYPE })
  }, [register, formStep])

  useEffect(() => {
    // Clear stale submit state on screen open or reload.
    resetSubmitInProgress()
  }, [resetSubmitInProgress])

  // Load DivisionCategories when company changes
  useEffect(() => {
    const companyId = watch('companyId')
    if (companyId && companyId !== 'new') {
      getDivisionCategories({ companyId })
    }
  }, [watch('companyId'), getDivisionCategories])

  // Filter division categories for the selected company (guard against undefined)
  const companyDivisionCategories = useMemo(() => {
    const companyId = watch('companyId')
    return (divisionCategories || []).filter((category) => category.companyId === companyId)
  }, [watch('companyId'), divisionCategories])

  // Check if the selected company has countries configured.
  const hasCountries = useMemo(() => {
    const companyId = watch('companyId')
    if (!companyId || companyId === 'new') return false
    const entry = (companyCountries || []).find((c) => c.companyId === companyId)
    return !!entry && entry.countries.length > 0
  }, [watch('companyId'), companyCountries])

  if (Capacitor.getPlatform() === 'ios') {
    useEffect(() => {
      let keyboardWillShowListener: PluginListenerHandle
      let keyboardDidShowListener: PluginListenerHandle
      let keyboardWillHideListener: PluginListenerHandle
      const addListeners = async () => {
        keyboardWillShowListener = await Keyboard.addListener('keyboardWillShow', (info) => {
          const buttonsWrapper = document.getElementsByClassName('buttons-wrapper') as HTMLCollectionOf<HTMLElement>
          if (buttonsWrapper[0]) buttonsWrapper[0].style.paddingBottom = info.keyboardHeight - 85 + 'px'
        })
        keyboardDidShowListener = await Keyboard.addListener('keyboardDidShow', (info) => {
          const customActivityNameInput = document.getElementById('customActivityName') as HTMLElement
          if (customActivityNameInput === document.activeElement) {
            scrollToBottom()
          }
        })
        keyboardWillHideListener = await Keyboard.addListener('keyboardWillHide', () => {
          const buttonsWrapper = document.getElementsByClassName('buttons-wrapper') as HTMLCollectionOf<HTMLElement>
          if (buttonsWrapper[0]) buttonsWrapper[0].style.paddingBottom = '0px'
        })
      }
      addListeners()
      return () => {
        if (keyboardWillShowListener) keyboardWillShowListener.remove()
        if (keyboardDidShowListener) keyboardDidShowListener.remove()
        if (keyboardWillHideListener) keyboardWillHideListener.remove()
      }
    }, [])
  }

  if (goalId) {
    useEffect(() => {
      getCurrentCompanyGoal({ id: goalId })
    }, [])

    const [goalExists, setGoalExists] = useState(false)
    useEffect(() => {
      if (goal) {
        const values = getDefaultValues(goals, goal) as FormData
        reset(values)
        setData(values)
        setGoalExists(true)
      }
    }, [goals])

    if (!goalExists) {
      return <Card>Challenge not found</Card>
    }
  }

  const cancel = () => {
    store.dispatch(push(`/challenges/${goalId}/info`))
  }

  const companyId = watch('companyId')
  const contractStartDate = getAccountStartDate(userCompanies, companyId)
  const contractEndDate = getAccountEndDate(userCompanies, companyId)

  const onSubmit = handleSubmit((newData) => {
    if (formStep === CHALLENGE_ADMIN_STEP_ORGANIZER) {
      newData.companyName = orderedCompanies.find((uc) => uc.companyId === newData.companyId)?.companyName || ''
      setValue('companyName', newData.companyName)

      const isUserCreatedCommunity = !!managedCompanies.find((uc) => uc.companyId === newData.companyId)?.ownerUserId
      setValue('isUserCreatedChallenge', newData.companyId === 'new' || isUserCreatedCommunity)

      // Logic if we have contract period.
      if (contractStartDate && contractEndDate) {
        const contractDays = calculateDuration(contractStartDate, contractEndDate)
        const contractMoreThanMonth = contractDays > 31
        const contractEnded = contractEndDate < new Date()

        // Set default value as contract period if less than 31 days and has not passed.
        if (!contractMoreThanMonth && !contractEnded) {
          const startDate = contractStartDate
          const endDate = dayjs(contractEndDate).endOf('day').toDate()
          const days = calculateDuration(startDate, endDate)
          setValue('dateRange', [startDate, endDate])
          setValue('days', days)
        }
      }

      // Check company level auto-join toggle.
      if (!goalId) {
        const autoJoinEnabledOnCompanyLevel = !!orderedCompanies.find(
          (uc: UserCompanyDto) => uc.companyId === newData.companyId,
        )?.autoJoinEnabled
        if (!newData.autoJoinEnabled && autoJoinEnabledOnCompanyLevel) {
          setValue('autoJoinEnabled', autoJoinEnabledOnCompanyLevel)
        }
      }
    }

    // After activity step.
    if (formStep === CHALLENGE_ADMIN_STEP_ACTIVITY_TYPE) {
      if (newData.activityType !== data.activityType && data.maxFormStep > formStep) {
        newData.maxFormStep = formStep
        setValue('maxFormStep', newData.maxFormStep)
      }
      if (!hasCountries) {
        // Remove country leaderboards when no countries are configured.
        const nextLeaderboards = (newData.leaderboards || []).filter(
          (leaderboardId) => ![ActivityView.COUNTRY].includes(leaderboardId as ActivityView),
        )
        if (nextLeaderboards.length === 0) {
          nextLeaderboards.push(ActivityView.INDIVIDUAL)
        }
        setValue('leaderboards', nextLeaderboards)
      }

      // If isCalories, make sure ActivityView.COLLECTIVE is enabled in leaderboards
      if (newData.activityType === CustomTypeCategory.CALORIES && !isLegacyChallenge) {
        if (!newData.leaderboards.includes(ActivityView.COLLECTIVE)) {
          //newData.leaderboards.push(ActivityView.COLLECTIVE)
          newData.leaderboards = [ActivityView.COLLECTIVE]
        }
        setValue('leaderboards', newData.leaderboards)
      }

      // If isSimpleDayType, set goalType to consistency (that's the only option)
      if (isSimpleDayType(newData.activityType as CustomTypeCategory)) {
        newData.goalType = 'consistentDays'
        setValue('goalType', newData.goalType)
      }
    }

    // After date step.
    if (formStep === CHALLENGE_ADMIN_STEP_PERIOD) {
      if (newData.days !== data.days && data.maxFormStep > formStep) {
        newData.maxFormStep = formStep
        setValue('maxFormStep', newData.maxFormStep)
      }

      // Total points challenge can not be consistency
      if (newData.goalType === 'consistentDays' && newData.activityType === CustomTypeCategory.TOTAL_POINTS) {
        setValue('goalType', '')
      }

      const defaultPersistenceGoal = Math.ceil(newData.days / 2)
      let newPersistenceGoal = newData.persistenceGoal || defaultPersistenceGoal
      if (newPersistenceGoal > newData.days) newPersistenceGoal = newData.days
      if (newData.persistenceGoal !== newPersistenceGoal) {
        newData.persistenceGoal = newPersistenceGoal
        setValue('persistenceGoal', newData.persistenceGoal)
      }

      if (newData.activityType === CustomTypeCategory.STEPS && !newData.dailyGoal) {
        newData.dailyGoal = 12000
        setValue('dailyGoal', newData.dailyGoal)
      }

      // Because goals are now optional...
      // if (newData.activityType === CustomTypeCategory.STEPS && !newData.totalGoal && newData.dailyGoal) {
      //   newData.totalGoal = Math.ceil(newData.days * newData.dailyGoal)
      //   setValue('totalGoal', newData.totalGoal)
      // }

      if (newData.activityType === CustomTypeCategory.ALL && !newData.indexTarget) {
        newData.indexTarget = 100
        setValue('indexTarget', newData.indexTarget)
      }

      if (
        newData.activityType === CustomTypeCategory.INTENSIVE_MINUTES &&
        (!newData.dailyGoal || newData.dailyGoal > 1440)
      ) {
        newData.dailyGoal = 20
        setValue('dailyGoal', newData.dailyGoal)
      }

      if (isSimpleDayType(newData.activityType as CustomTypeCategory)) {
        newData.dailyGoal = 1
        setValue('dailyGoal', newData.dailyGoal)
      }

      if (!newData.collectiveGoal) {
        newData.collectiveGoal = 1000000
        setValue('collectiveGoal', newData.collectiveGoal)
      }

      const selectedCustomTypeExists = customUnits.some(
        (cu) => cu.category === newData.activityType && cu.id === newData.customType,
      )
      if (!newData.customType || !selectedCustomTypeExists) {
        const customUnitsForActivity = customUnits.filter((cu) => cu.category === newData.activityType)
        // Prefer Miles as default for US time zones when available.
        const milesUnit = isUsa ? customUnitsForActivity.find((cu) => `${cu.id}`.endsWith('_MI')) : undefined
        newData.customType = milesUnit?.id || customUnitsForActivity[0]?.id || null
        setValue('customType', newData.customType)
      }

      if (newData.activityType === CustomTypeCategory.POINTS) {
        const relevantTotalPointChallenges = filterTotalChallengesThatCover(
          companyId,
          goals,
          newData.dateRange[0],
          newData.dateRange[1],
        )
        const data = {} as Record<GoalId, boolean>
        relevantTotalPointChallenges.forEach((challenge) => {
          data[challenge.id] = goalId ? challenge.pointsLogic?.includes(goalId) : true
        })
        setValue('includeInTotalPointsChallenges', data)
      }
    }

    // After leaderboards step.
    if (formStep === CHALLENGE_ADMIN_STEP_LEADERBOARDS) {
      if (newData.dailyGoal === 0) {
        newData.dailyGoal = ''
        setValue('dailyGoal', newData.dailyGoal)
      }

      // Persist enabled leaderboards in their current order.
      const draft = newData.leaderboardsDraft || []
      const enabledLeaderboards = draft.filter((item) => item.enabled).map((item) => item.id)
      const ensuredLeaderboards = enabledLeaderboards.length ? enabledLeaderboards : [ActivityView.INDIVIDUAL]
      newData.leaderboards = ensuredLeaderboards
      setValue('leaderboards', ensuredLeaderboards)
    }

    // TODO: After challenge type step - backwards compatibility
    // if (formStep === CHALLENGE_ADMIN_STEP_CHALLENGE_TYPE) {
    // }

    // Backwards compatibility
    // if (formStep === CHALLENGE_ADMIN_STEP_GOAL) {
    // 1. If total target, individual total goal exists -> legacy total goal (customGoal)
    if (newData.goals.individual?.totalGoal) {
      newData.totalGoal = newData.goals.individual?.totalGoal
      setValue('totalGoal', newData.totalGoal)
    }
    if (newData.goals.individual?.indexTarget) {
      newData.indexTarget = newData.goals.individual?.indexTarget
      setValue('indexTarget', newData.indexTarget)
    }
    // 2. If total target, collective total goal exists -> legacy collective goal (collectiveGoal)
    if (newData.goals.collective?.totalGoal) {
      newData.collectiveGoal = newData.goals.collective?.totalGoal
      setValue('collectiveGoal', newData.collectiveGoal)
    }
    // 3. If daily target, individual total goal exists -> legacy fields (dailyPersistenceGoal & persistenceGoal)
    // if (newData.goals.individual?.dailyGoal) {
    //   newData.dailyGoal = newData.goals.individual?.dailyGoal
    //   setValue('dailyGoal', newData.dailyGoal)
    // }
    if (newData.goals.individual?.persistenceGoal) {
      newData.persistenceGoal = newData.goals.individual?.persistenceGoal
      setValue('persistenceGoal', newData.persistenceGoal)
    }
    //}

    // For some reason this needs to be done after every step.
    if (newData.activityType === CustomTypeCategory.POINTS) {
      newData.challengePoints = newData.challengePoints.map((logic) => {
        const result = { ...logic }

        // Only include dailyGoal if dailyGoalEnabled is true
        if (logic.dailyGoalEnabled) {
          result.dailyGoal = logic.dailyGoal
        } else {
          delete result.dailyGoal
        }

        // Only include totalMax if totalMaxEnabled is true
        if (logic.totalMaxEnabled) {
          result.totalMax = logic.totalMax
        } else {
          delete result.totalMax
        }

        return result
      })
    }

    let next = formStep + 1

    // Skip POINTS step if activity type is not POINTS
    if (next === CHALLENGE_ADMIN_STEP_POINTS) {
      const activityType = newData.activityType || watch('activityType')
      if (activityType !== CustomTypeCategory.POINTS) {
        next = CHALLENGE_ADMIN_STEP_PERIOD
      }
    }

    // Skip TOTAL_POINTS step if activity type is not TOTAL_POINTS
    if (next === CHALLENGE_ADMIN_STEP_TOTAL_POINTS) {
      const activityType = newData.activityType || watch('activityType')
      if (activityType !== CustomTypeCategory.TOTAL_POINTS) {
        next = CHALLENGE_ADMIN_STEP_LEADERBOARDS
      }
    }

    if (formStep < formSteps && newData.maxFormStep < next) {
      newData.maxFormStep = next
      setValue('maxFormStep', newData.maxFormStep)
    }

    setData(newData)

    if (formStep < formSteps) {
      setFormStep(next)
      // Scroll to top when navigating to next step
      scrollToTop()
    } else {
      createUpdateChallenge()
    }
  })

  const prevStep = () => {
    let prev = formStep - 1

    // Skip POINTS step if activity type is not POINTS
    if (prev === CHALLENGE_ADMIN_STEP_POINTS) {
      const activityType = watch('activityType')
      if (activityType !== CustomTypeCategory.POINTS) {
        prev = CHALLENGE_ADMIN_STEP_ACTIVITY_TYPE
      }
    }

    // Skip TOTAL_POINTS step if activity type is not TOTAL_POINTS
    if (prev === CHALLENGE_ADMIN_STEP_TOTAL_POINTS) {
      const activityType = watch('activityType')
      if (activityType !== CustomTypeCategory.TOTAL_POINTS) {
        prev = CHALLENGE_ADMIN_STEP_PERIOD
      }
    }

    if (prev >= formStartStep) {
      setFormStep(prev)
      clearErrors()
      // Scroll to top when navigating back
      scrollToTop()
    }
  }

  const nextStep = () => {
    onSubmit()
  }

  const getStepTitle = (step: number) => {
    switch (step) {
      case CHALLENGE_ADMIN_STEP_ORGANIZER:
        return t`Choose organizer`
      case CHALLENGE_ADMIN_STEP_ACTIVITY_TYPE:
        return t`Choose activity type`
      case CHALLENGE_ADMIN_STEP_POINTS:
        return t`Configure points logic`
      case CHALLENGE_ADMIN_STEP_PERIOD:
        return t`Choose period`
      case CHALLENGE_ADMIN_STEP_TOTAL_POINTS:
        return t`Wellness points challenges`
      case CHALLENGE_ADMIN_STEP_LEADERBOARDS:
        return t`Choose leaderboards`
      case CHALLENGE_ADMIN_STEP_CHALLENGE_TYPE:
        return t`Choose challenge type`
      case CHALLENGE_ADMIN_STEP_GOAL:
        return t`Choose goals`
      case CHALLENGE_ADMIN_STEP_CONFIGURATION:
        return t`Choose configuration`
      case CHALLENGE_ADMIN_STEP_DETAILS:
        return t`Set details`
      case CHALLENGE_ADMIN_STEP_REVIEW:
        return t`Review challenge`
    }
    return ''
  }

  const activityType = watch('activityType')
  const isUserCreatedChallenge = watch('isUserCreatedChallenge')

  const createUpdateChallenge = () => {
    // TODO:
    const id = false
    const {
      // ORGANIZER.
      companyId,
      newCommunityName,

      // ACTIVITY TYPE.
      activityType,
      customActivityName,

      // POINTS.
      leaderboards,
      chatEnabled,
      manualEntryEnabled,
      autoJoinEnabled,
      maxManualEntryDays,
      roundDailyPoints,

      challengePoints,
      challengeTotalPoints,
      includeInTotalPointsChallenges,

      // PERIOD.
      dateRange,
      days,

      // GOALS.
      goalType,
      goals: goalsConfig,
      avgAgg,

      totalGoal,
      dailyMax,
      collectiveGoal: collectiveGoalRaw,
      persistenceGoal: persistenceGoalRaw,
      dailyGoal: dailyGoalRaw,
      customUnit: customUnitRaw,
      customType: customTypeRaw,
      indexTarget,

      // DETAILS.
      title,
      prize: voucher,
      description,
      simpleManualEntry,
      showGroupMembers,
      showGroupMembersContributions,
      hideLeaderboardTables,
    } = data
    let progressType: any = activityType

    let customType = activityType === CustomTypeCategory.CUSTOM ? CustomType.CUSTOM : null
    const customName = activityType === CustomTypeCategory.CUSTOM ? (customActivityName as string) : null
    let customUnit = customUnitRaw || null

    // Popular custom challenges - all except activity index.
    if (!isActivityIndexType(activityType as ProgressType) && customType !== CustomType.CUSTOM) {
      progressType = CustomTypeCategory.CUSTOM
      customType = customTypeRaw
      customUnit = null

      // Backwards compatibility.
      if (!customType) {
        if (activityType === CustomTypeCategory.STEPS) {
          customType = CustomType.STEPS
        } else if (activityType === CustomTypeCategory.INTENSIVE_MINUTES) {
          customType = CustomType.INTENSIVE_MINUTES
        } else if (activityType === CustomTypeCategory.CALORIES) {
          customType = CustomType.CALORIES
        }
      }
    }

    // Consistency challenges.
    if (goalType === 'consistentDays') {
      if (activityType === CustomTypeCategory.ALL) {
        progressType = ProgressType.PERSISTENCE_ALL
      } else {
        progressType = ProgressType.PERSISTENCE_CUSTOM
      }
    }

    // Collective challenges.
    if (goalType === 'collective') {
      progressType = ProgressType.COLLECTIVE_CUSTOM
    }

    // Needed for backwards compatibility.
    if (activityType === CustomTypeCategory.CUSTOM && simpleManualEntry /*&& goalType === 'consistentDays'*/) {
      progressType = ProgressType.PERSISTENCE_CUSTOM_BINARY
    }

    const startDate = dateRange[0] ? convertLocalToUTCDate(dateRange[0]).getTime() : 0
    const endDate = dateRange[1] ? convertLocalToUTCDate(dateRange[1]).getTime() : 0

    // TODO: In the future, we could support division specific challenges.
    const divisionId = null

    const dailyStepsGoal = progressType === CustomTypeCategory.STEPS ? dailyGoalRaw || null : null
    const activityFormulaTarget = progressType === CustomTypeCategory.ALL ? indexTarget : null
    const collectiveGoal =
      /*asCollectiveUnit*/ goalType === 'collective' && collectiveGoalRaw ? collectiveGoalRaw : null //parseInt(collectiveGoalRaw as string)

    // Determine which tabs should be hidden based on the ordered list.
    const hidePeopleTab = !leaderboards.includes(ActivityView.INDIVIDUAL)
    const hideGroupsTab = !leaderboards.includes(ActivityView.GROUP)
    const hideCountriesTab = !leaderboards.includes(ActivityView.COUNTRY)

    let dailyPersistenceGoal = ['consistentDays'].includes(goalType)
      ? progressType === ProgressType.PERSISTENCE_ALL
        ? indexTarget || null
        : dailyGoalRaw || null
      : null

    // TODO: Remove this after backend bug is fixed.
    if ([ProgressType.PERSISTENCE_CUSTOM_BINARY].includes(progressType)) {
      dailyPersistenceGoal = 0
    }

    const persistenceGoal = isPersistenceType(progressType) ? persistenceGoalRaw || null : 0
    const customGoal =
      (isCustomType(progressType) || progressType === CustomTypeCategory.INTENSIVE_MINUTES) && totalGoal
        ? totalGoal
        : null

    let pointsLogic = activityType === ProgressType.POINTS ? challengePoints : null

    if (pointsLogic && pointsLogic.length > 0) {
      // Remove icon from pointsLogic if it exists and is not null
      pointsLogic = pointsLogic.map((item) => {
        const { icon, ...rest } = item
        return icon !== null ? item : rest
      })
    }

    const totalPointsLogic = activityType === ProgressType.TOTAL_POINTS ? challengeTotalPoints : null

    // Keep the ordered list as-is.
    // This now includes standard leaderboard IDs and division categories.
    const orderedLeaderboards = leaderboards || []

    const payload = {
      voucher,
      progressType,
      endDate,
      startDate,
      companyId,
      newCommunityName,
      divisionId,
      dailyStepsGoal,
      activityFormulaTarget,
      collectiveGoal,
      title,
      hidePeopleTab,
      hideGroupsTab,
      hideCountriesTab,
      chatEnabled,
      dailyPersistenceGoal,
      persistenceGoal,
      customType,
      customName,
      customUnit,
      customGoal,
      description,
      manualEntryEnabled,
      pointsLogic,
      totalPointsLogic,
      roundDailyPoints,
      autoJoinEnabled,
      maxManualEntryDays,
      simpleManualEntry,
      includeInTotalPointsChallenges,
      dailyMax: dailyMax && dailyMax > 0 ? dailyMax : null,
      avgAgg,
      // TODO: Don't set goals and leaderboards if isLegacyChallenge
      leaderboards: isLegacyChallenge ? [] : orderedLeaderboards,
      goals: isLegacyChallenge ? null : goalsConfig,
      showGroupMembers,
      showGroupMembersContributions,
      hideLeaderboardTables,
    }

    if (!goalId) {
      props.createGoal(payload)
    } else {
      props.updateGoal({
        goalId,
        ...payload,
      })
    }
  }

  const contractPeriod = getContractPeriod(managedCompanies, companyId)

  const creationForm = (
    <form
      onSubmit={onSubmit}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ flexGrow: 1 }}>
        <Hidden mdUp>
          <LinearProgress
            variant="determinate"
            value={((formStep + 1 - formStartStep) / (formSteps + 1 - formStartStep)) * 100}
            style={{ marginBottom: 20 }}
          />
        </Hidden>
        <Typography
          variant="subtitle2"
          style={{ marginBottom: formStep === CHALLENGE_ADMIN_STEP_GOAL ? '3px' : '24px' }}
        >
          {getStepTitle(formStep)}
        </Typography>

        {formStep === CHALLENGE_ADMIN_STEP_ORGANIZER && (
          <ChallengeStepOrganizer
            control={control}
            register={register}
            errors={errors}
            formStep={formStep}
            companyId={companyId}
            userId={props.userDto.userId}
            activityType={activityType as CustomTypeCategory}
            managedCompanies={orderedCompanies}
            userCompanies={userCompanies}
            contractPeriod={contractPeriod}
          />
        )}
        {formStep === CHALLENGE_ADMIN_STEP_ACTIVITY_TYPE && (
          <ChallengeStepActivityType
            setValue={setValue}
            register={register}
            errors={errors}
            formStep={formStep}
            activityType={activityType as CustomTypeCategory}
            control={control}
            watch={watch}
          />
        )}
        {formStep === CHALLENGE_ADMIN_STEP_POINTS && (
          <ChallengeStepPoints
            register={register}
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
            activityType={activityType as CustomTypeCategory}
            goals={goals}
            companyId={companyId}
            userCompanies={userCompanies}
            isEdit={!!goalId}
          />
        )}
        {formStep === CHALLENGE_ADMIN_STEP_PERIOD && (
          <ChallengeStepPeriod
            control={control}
            errors={errors}
            setValue={setValue}
            companyId={companyId}
            userCompanies={userCompanies}
            contractPeriod={contractPeriod}
            contractStartDate={contractStartDate}
            contractEndDate={contractEndDate}
            isUserCreatedChallenge={isUserCreatedChallenge}
          />
        )}
        {formStep === CHALLENGE_ADMIN_STEP_TOTAL_POINTS && (
          <ChallengeStepTotalPoints
            register={register}
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
            activityType={activityType as CustomTypeCategory}
            goals={goals}
            companyId={companyId}
          />
        )}
        {formStep === CHALLENGE_ADMIN_STEP_LEADERBOARDS && (
          <ChallengeStepLeaderboards
            register={register}
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
            hasCountries={hasCountries}
            divisionCategories={companyDivisionCategories}
            isLegacyChallenge={isLegacyChallenge}
          />
        )}
        {formStep === CHALLENGE_ADMIN_STEP_CHALLENGE_TYPE && (
          <ChallengeStepChallengeType
            register={register}
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
            originalProgressType={goal?.progressType || null}
          />
        )}
        {formStep === CHALLENGE_ADMIN_STEP_GOAL && (
          <ChallengeStepGoal
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
            activityType={activityType as CustomTypeCategory}
            formStep={formStep}
            divisionCategories={companyDivisionCategories}
            isLegacyChallenge={isLegacyChallenge}
          />
        )}
        {formStep === CHALLENGE_ADMIN_STEP_CONFIGURATION && (
          <ChallengeStepConfiguration
            register={register}
            control={control}
            errors={errors}
            watch={watch}
            setValue={setValue}
            activityType={activityType as CustomTypeCategory}
            isUserCreatedChallenge={isUserCreatedChallenge}
            userCompanies={userCompanies}
            formStep={formStep}
          />
        )}
        {formStep === CHALLENGE_ADMIN_STEP_DETAILS && (
          <ChallengeStepDetails control={control} errors={errors} formStep={formStep} />
        )}
        {formStep === CHALLENGE_ADMIN_STEP_REVIEW && (
          <div style={{ marginBottom: 20 }}>
            <ChallengeReview
              editMode={!!goalId}
              data={data}
              formStep={formStep}
              setFormStep={setFormStep}
              userCompanies={userCompanies}
              goals={goals}
              divisionCategories={companyDivisionCategories}
              isLegacyChallenge={isLegacyChallenge}
            />
          </div>
        )}
      </div>

      <div style={{ marginTop: '20px', textAlign: 'center', pointerEvents: 'none' }}>
        <div
          style={{
            minHeight: '48px',
          }}
        >
          <div className={!areButtonsVisible ? 'buttons-wrapper buttons-wrapper__sticky' : 'buttons-wrapper'}>
            {goal && formStep === CHALLENGE_ADMIN_STEP_ACTIVITY_TYPE ? (
              <YumCancelButton
                onClick={cancel}
                submitInProgress={props.submitInProgress}
                style={{ pointerEvents: 'auto' }}
              />
            ) : (
              ''
            )}
            {formStep > formStartStep && (
              <YumCancelButton
                text={t`Back`}
                onClick={prevStep}
                submitInProgress={props.submitInProgress}
                style={{ pointerEvents: 'auto' }}
              />
            )}
            {/* Use global submit state to show loader and prevent double submits. */}
            <YumSubmitButton
              text={formStep === formSteps ? (goalId ? t`Save` : t`Create`) : t`Next`}
              submitInProgress={props.submitInProgress}
              style={{ pointerEvents: 'auto' }}
            />
          </div>
        </div>
        <div ref={buttonsRef} style={{ position: 'relative', top: 40 }}></div>
        {/*<br />
        Render count: {renderCount}*/}
      </div>
    </form>
  )

  return (
    <Card>
      {/*<h1 style={{ marginBottom: 40, whiteSpace: 'nowrap' }}>{t`Create challenge`}</h1>*/}
      <Grid container={true} spacing={10} style={{ minHeight: 'calc(100vh - 80px)' }}>
        <Grid
          item={true}
          xs={12}
          md={formStep < CHALLENGE_ADMIN_STEP_REVIEW ? 6 : 12}
          style={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '528px',
            marginLeft: 'auto',
            marginRight: 'auto',
            //height: 'calc(100vh - 80px)',
          }}
        >
          <NotificationsWrapper />
          {creationForm}
        </Grid>
        {formStep < CHALLENGE_ADMIN_STEP_REVIEW && (
          <Hidden smDown>
            <Grid
              item={true}
              xs={12}
              md={6}
              style={isRtl ? { borderRight: `1px solid ${gray}` } : { borderLeft: `1px solid ${gray}` }}
            >
              <Typography variant="subtitle2" style={{ marginBottom: '24px' }}>
                {t`Challenge overview`}
              </Typography>
              <ChallengeReview
                editMode={!!goalId}
                data={data}
                formStep={formStep}
                setFormStep={setFormStep}
                userCompanies={userCompanies}
                goals={goals}
                divisionCategories={divisionCategories}
                isLegacyChallenge={isLegacyChallenge}
              />
            </Grid>
          </Hidden>
        )}
      </Grid>
      {/*<pre>{JSON.stringify(data, null, 2)}</pre>*/}
    </Card>
  )
}
